import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './signin.css'; // Reuse signin styles
import logo from './images/KrossKast_Logo-red.png';
import backgroundImage from './images/sign-up-desk-background-image.jpg';

/**
 * Stage API base (custom domain). `confirm-email` is a canonical domain-routing
 * mapping in every stage, so the confirmation call must follow the stage, not a
 * hardcoded staging execute-api host.
 */
function confirmEmailUrl(token) {
  const base = String(process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  if (!base) {
    throw new Error('REACT_APP_API_BASE_URL is not configured for this stage');
  }
  return `${base}/confirm-email?token=${encodeURIComponent(token)}`;
}

const ConfirmEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('confirming'); // confirming, success, error
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No confirmation token provided. Please check your email for the correct link.');
      return;
    }

    let timer = null;

    // Call the API to confirm the email
    const confirmEmail = async () => {
      try {
        const response = await fetch(
          confirmEmailUrl(token),
          {
            method: 'GET',
            headers: {
              'Accept': 'text/html,application/json',
            },
          }
        );

        if (response.ok) {
          setStatus('success');
          setMessage('Your email has been confirmed! Your 14-day free trial has started.');
          
          // Start countdown timer
          timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate('/signin?confirmed=true&trial=true');
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setStatus('error');
          setMessage(errorData.message || 'Failed to confirm email. Please try again or contact support.');
        }
      } catch (error) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setMessage('Network error. Please check your connection and try again.');
      }
    };

    confirmEmail();

    // Cleanup timer if component unmounts
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [searchParams, navigate]);

  return (
    <div className="signin-page">
      <div className="top-nav" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <img src={logo} alt="KrossKast" className="nav-logo" />
      </div>
      <div className="signin-container" style={{ 
        backgroundImage: `url(${backgroundImage})`, 
        backgroundSize: 'cover', 
        backgroundPosition: 'center', 
        minHeight: '100vh' 
      }}>
        <div className="signin-form-container" style={{ textAlign: 'center' }}>
          {status === 'confirming' && (
            <>
              <h1>Confirming Your Email...</h1>
              <div style={{ margin: '20px 0' }}>
                <div className="loading-spinner" style={{
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #e53e3e',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }}></div>
              </div>
              <p>Please wait while we confirm your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ fontSize: '48px', color: '#4caf50', marginBottom: '20px' }}>✅</div>
              <h1 style={{ color: '#4caf50' }}>Email Confirmed!</h1>
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>{message}</p>
              <div style={{
                backgroundColor: '#e8f5e8',
                border: '1px solid #4caf50',
                borderRadius: '4px',
                padding: '15px',
                margin: '20px 0'
              }}>
                <h3>🎉 Your 14-Day Free Trial Includes:</h3>
                <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                  <li>Full access to your Master Account dashboard</li>
                  <li>Complete music management tools</li>
                  <li>Location and channel management</li>
                  <li>Real-time analytics and reporting</li>
                  <li>Priority customer support</li>
                </ul>
              </div>
              <p><small>Redirecting to sign in page in {countdown} seconds...</small></p>
              <button 
                onClick={() => navigate('/signin?confirmed=true&trial=true')}
                className="signin-btn"
                style={{ marginTop: '10px' }}
              >
                Sign In Now
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ fontSize: '48px', color: '#f44336', marginBottom: '20px' }}>❌</div>
              <h1 style={{ color: '#f44336' }}>Confirmation Failed</h1>
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>{message}</p>
              <div style={{
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                padding: '15px',
                margin: '20px 0'
              }}>
                <p><strong>Need help?</strong></p>
                <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                  <li>Check if the link has expired (valid for 24 hours)</li>
                  <li>Make sure you're using the complete link from your email</li>
                  <li>Try signing up again if the link is too old</li>
                  <li>Contact our support team for assistance</li>
                </ul>
              </div>
              <button 
                onClick={() => navigate('/signup')}
                className="signin-btn"
                style={{ marginTop: '10px', marginRight: '10px' }}
              >
                Sign Up Again
              </button>
              <button 
                onClick={() => navigate('/signin')}
                className="signin-btn"
                style={{ marginTop: '10px', backgroundColor: '#666' }}
              >
                Back to Sign In
              </button>
            </>
          )}
        </div>
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConfirmEmail;
